import {
  IDocumentAssemblerTemplateVersionConditionalTree,
  IDocumentAssemblerTemplateVersionDetail,
  IDocumentAssemblerTemplateVersionPlaceholder,
} from '@commenda/api';
import { Alert, Badge } from '@commenda/kakashi';
import { pluralize } from '@commenda/utils';
import { useMemo } from 'react';

interface DocumentAssemblerVersionInventoryCardProps {
  version: IDocumentAssemblerTemplateVersionDetail;
}

interface PlaceholderListProps {
  placeholders: IDocumentAssemblerTemplateVersionPlaceholder[];
  emptyLabel: string;
}

const PlaceholderList = ({
  placeholders,
  emptyLabel,
}: PlaceholderListProps) => {
  if (placeholders.length === 0) {
    return (
      <p className="txt-extra-small-regular text-text-tertiary">{emptyLabel}</p>
    );
  }

  return (
    <div className="flex flex-col gap-xs">
      {placeholders.map((placeholder) => (
        <div key={placeholder.id} className="flex items-start gap-sm">
          <span className="txt-extra-small-regular w-8 shrink-0 text-right font-mono text-text-tertiary">
            {placeholder.ordinal + 1}
          </span>
          <p className="txt-small-regular whitespace-pre-wrap text-text-primary">
            {placeholder.instruction}
          </p>
        </div>
      ))}
    </div>
  );
};

interface ConditionalTreeSectionProps {
  tree: IDocumentAssemblerTemplateVersionConditionalTree;
  position: number;
  placeholdersByBranchId: Map<
    string,
    IDocumentAssemblerTemplateVersionPlaceholder[]
  >;
}

const ConditionalTreeSection = ({
  tree,
  position,
  placeholdersByBranchId,
}: ConditionalTreeSectionProps) => (
  <div className="flex flex-col gap-md rounded-lg border border-border-tertiary p-lg">
    <span className="txt-extra-small-medium text-text-tertiary">
      Conditional block {position}
    </span>
    <div className="flex flex-col gap-md">
      {tree.branches.map((branch) => (
        <div key={branch.id} className="flex flex-col gap-xs">
          <div className="flex flex-wrap items-baseline gap-sm">
            <Badge variant="blue" size="sm">
              {branch.kind}
            </Badge>
            <span className="txt-small-regular text-text-secondary">
              {/* An ELSE branch carries no condition: it is the fallback. */}
              {branch.condition ?? 'when no branch above applies'}
            </span>
          </div>
          <div className="pl-xl">
            <PlaceholderList
              placeholders={placeholdersByBranchId.get(branch.id) ?? []}
              emptyLabel="Asks for nothing — this branch only decides which prose survives."
            />
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const DocumentAssemblerVersionInventoryCard = ({
  version,
}: DocumentAssemblerVersionInventoryCardProps) => {
  const { placeholdersByBranchId, unconditionalPlaceholders } = useMemo(() => {
    const byBranchId = new Map<
      string,
      IDocumentAssemblerTemplateVersionPlaceholder[]
    >();
    const branchIds = new Set(
      version.conditionalTrees.flatMap((tree) =>
        tree.branches.map((branch) => branch.id)
      )
    );
    const unconditional: IDocumentAssemblerTemplateVersionPlaceholder[] = [];

    version.placeholders.forEach((placeholder) => {
      // A branch id that names no branch on this version should be impossible — it is a real
      // foreign key. Counting it as unconditional keeps every placeholder visible rather than
      // letting one vanish between the two groups.
      if (
        placeholder.conditionalBranchId &&
        branchIds.has(placeholder.conditionalBranchId)
      ) {
        const existing = byBranchId.get(placeholder.conditionalBranchId) ?? [];
        byBranchId.set(placeholder.conditionalBranchId, [
          ...existing,
          placeholder,
        ]);
        return;
      }

      unconditional.push(placeholder);
    });

    return {
      placeholdersByBranchId: byBranchId,
      unconditionalPlaceholders: unconditional,
    };
  }, [version.placeholders, version.conditionalTrees]);

  const asksForNothing =
    version.placeholders.length === 0 && version.conditionalTrees.length === 0;

  return (
    <div className="flex flex-col gap-lg rounded-xl border border-border-primary p-xl">
      <div className="flex flex-col gap-xs">
        <div className="flex flex-wrap items-center gap-sm">
          <h3 className="txt-medium-medium text-text-primary">
            What this file asks for
          </h3>
          <Badge variant="gray" size="sm">
            {pluralize(version.placeholders.length, 'placeholder')}
          </Badge>
          {version.conditionalTrees.length > 0 ? (
            <Badge variant="gray" size="sm">
              {pluralize(version.conditionalTrees.length, 'conditional block')}
            </Badge>
          ) : null}
        </div>
        <p className="txt-extra-small-regular text-text-secondary">
          Read out of the file when it was uploaded. The expert is asked for
          each of these, in this order, and writes the answers into the
          document.
        </p>
      </div>

      {version.errors.length > 0 ? (
        <Alert
          variant="warning"
          description="This file has problems, so what was read out of it may be incomplete. Fix them and replace the file to see the full list."
        />
      ) : null}

      {asksForNothing ? (
        <p className="txt-small-regular text-text-tertiary">
          Nothing. This file has no placeholders and no conditional blocks, so
          every document produced from it would be identical.
        </p>
      ) : (
        <>
          <div className="flex flex-col gap-sm">
            <span className="txt-extra-small-medium text-text-tertiary">
              Always asked
            </span>
            <PlaceholderList
              placeholders={unconditionalPlaceholders}
              emptyLabel="Nothing is asked for unconditionally — every placeholder sits inside a conditional block."
            />
          </div>

          {version.conditionalTrees.map((tree, index) => (
            <ConditionalTreeSection
              key={tree.id}
              tree={tree}
              position={index + 1}
              placeholdersByBranchId={placeholdersByBranchId}
            />
          ))}
        </>
      )}
    </div>
  );
};
